/**
 * Notifications Hook
 * Reusable hooks for admin notification management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '@/api/api.client';
import { API_ENDPOINTS } from '@/api/endpoints';

// Query keys
export const NOTIFICATIONS_KEY = ['notifications'];
export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];

// Backend returns i18n fields
interface RawNotification {
  id: number;
  type: string;
  titleFr: string;
  titleEn: string;
  messageFr: string;
  messageEn: string;
  isRead: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Frontend uses resolved title/message based on current language
export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** Map backend i18n fields to single title/message based on language */
function mapNotification(raw: RawNotification, lang: string): Notification {
  const isFr = lang === 'fr';
  return {
    id: raw.id,
    type: raw.type,
    title: isFr ? (raw.titleFr || raw.titleEn) : (raw.titleEn || raw.titleFr),
    message: isFr ? (raw.messageFr || raw.messageEn) : (raw.messageEn || raw.messageFr),
    isRead: raw.isRead,
    metadata: raw.metadata,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

interface NotificationsApiResponse {
  success: boolean;
  data: RawNotification[];
  meta: {
    timestamp: string;
    path: string;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface UnreadCountApiResponse {
  success: boolean;
  data: { count: number };
  meta: {
    timestamp: string;
    path: string;
  };
}

export interface NotificationsPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedNotifications {
  data: Notification[];
  meta: NotificationsPaginationMeta;
}

/**
 * Fetch paginated notifications for the current user
 */
export function useNotifications(page = 1, limit = 20) {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return useQuery<NotificationsApiResponse, Error, PaginatedNotifications>({
    queryKey: [...NOTIFICATIONS_KEY, { page, limit, lang }],
    queryFn: async () => {
      const url = `${API_ENDPOINTS.NOTIFICATIONS.BASE}?page=${page}&limit=${limit}`;
      const response = await apiClient.get<NotificationsApiResponse>(url);
      return response.data;
    },
    select: (apiResponse) => ({
      data: apiResponse.data.map((n) => mapNotification(n, lang)),
      meta: apiResponse.meta.pagination,
    }),
    staleTime: 30000,
  });
}

/**
 * Fetch unread notification count
 * Auto-refetches every 30 seconds for real-time badge updates
 */
export function useUnreadCount() {
  return useQuery<UnreadCountApiResponse, Error, { count: number }>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => {
      const response = await apiClient.get<UnreadCountApiResponse>(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
      );
      return response.data;
    },
    select: (data) => data.data,
    refetchInterval: 30000,
    staleTime: 15000,
  });
}

/**
 * Mark a single notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.READ(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: async () => {
      await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.READ_ALL);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

/**
 * Delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await apiClient.delete(API_ENDPOINTS.NOTIFICATIONS.DELETE(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export default useNotifications;
